import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session, User, AuthError } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { setActiveAgencySession } from "@/features/agency/session-store";

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  /** True until the initial session-restore from storage completes. */
  isLoading: boolean;
  /** Non-null when the initial session restore could not complete. */
  initializationError: Error | null;

  signInWithPassword: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  /**
   * Self-service password reset. Routes through the `relay-request-password-reset`
   * Edge Function (server-side recovery link + Relay-branded email) rather than
   * the browser's `resetPasswordForEmail`, which sends the shared project's
   * Forge-branded email and issues a PKCE link that only works in the originating
   * browser. The redirect target is pinned server-side, so no redirectTo here.
   */
  requestPasswordReset: (email: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// A browser storage lock or a failed auth-client initialization must never
// leave every protected route on its loading shell forever. This is long
// enough for normal local session hydration, while still giving the user a
// visible recovery path when initialization becomes stuck.
const AUTH_INITIALIZATION_TIMEOUT_MS = 10_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [initializationError, setInitializationError] = useState<Error | null>(null);

  // Hydrate session from storage on mount, then subscribe to auth changes.
  // Both happen in one effect so the listener is set up before we await
  // getSession() — no race where SIGNED_IN events fire while we're still
  // resolving the initial state.
  useEffect(() => {
    let mounted = true;
    let initialized = false;
    let initializationTimeout: number | undefined;

    const clearInitializationTimeout = () => {
      if (initializationTimeout !== undefined) {
        window.clearTimeout(initializationTimeout);
        initializationTimeout = undefined;
      }
    };

    const applySession = (nextSession: Session | null) => {
      if (!mounted) return;
      initialized = true;
      clearInitializationTimeout();
      setActiveAgencySession((current) => {
        if (!nextSession?.user || (current && current.authUserId !== nextSession.user.id)) {
          return null;
        }
        return current;
      });
      setSession(nextSession);
      setInitializationError(null);
      setIsLoading(false);
    };

    const failInitialization = (cause: unknown) => {
      if (!mounted || initialized) return;
      clearInitializationTimeout();
      const error = cause instanceof Error
        ? cause
        : new Error("Relay could not restore your sign-in session.");
      console.error("[AuthProvider] Session initialization failed:", error);
      setInitializationError(error);
      setIsLoading(false);
    };

    initializationTimeout = window.setTimeout(() => {
      failInitialization(
        new Error("Relay took too long to restore your sign-in session."),
      );
    }, AUTH_INITIALIZATION_TIMEOUT_MS);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      // The initial event from onAuthStateChange happens before getSession
      // resolves on most paths; safe to mark not-loading here too.
      applySession(nextSession);
    });

    void supabase.auth.getSession()
      .then(({ data, error }) => {
        if (error) {
          failInitialization(error);
          return;
        }
        applySession(data.session);
      })
      .catch(failInitialization);

    return () => {
      mounted = false;
      clearInitializationTimeout();
      subscription.unsubscribe();
    };
  }, []);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    // Unknown/non-Relay addresses always get { ok: true } (anti-enumeration). A
    // non-null error means transport failure or an operational error after a valid
    // Relay user was resolved.
    const { error } = await supabase.functions.invoke("relay-request-password-reset", {
      body: { email },
    });
    return { error: error as Error | null };
  }, []);

  const signOut = useCallback(async () => {
    setActiveAgencySession(null);
    await supabase.auth.signOut();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      isLoading,
      initializationError,
      signInWithPassword,
      requestPasswordReset,
      signOut,
    }),
    [session, isLoading, initializationError, signInWithPassword, requestPasswordReset, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
